<template>
<div>
    <h1>Réglages</h1>
        <div class="settingsTabs">
            <InputButton label="Réservation" class="p-button-sm" :class="tabButtonClass('booking')" @click="setActiveSettingsTab('booking')" />
            <InputButton label="Tarifs" class="p-button-sm" :class="tabButtonClass('pricing')" @click="setActiveSettingsTab('pricing')" />
            <InputButton label="Info publique" class="p-button-sm" :class="tabButtonClass('publicInfo')" @click="setActiveSettingsTab('publicInfo')" />
            <InputButton label="Compte" class="p-button-sm" :class="tabButtonClass('account')" @click="setActiveSettingsTab('account')" />
        </div>

        <CardView class="settingsCard" v-if="activeSettingsTab !== 'account'">

        <template #header>
            <h2 v-if="activeSettingsTab === 'booking'">Paramètres de réservation</h2>
            <h2 v-else-if="activeSettingsTab === 'pricing'">Tarifs du calculateur</h2>
            <h2 v-else>Bloc d'information public</h2>
        </template>
        <template #content>
            <GridLoader v-if="settingsLoading" class="loader"></GridLoader>

            <div v-if="activeSettingsTab === 'booking'" class="p-fluid clientdata grid settingsGrid">
                <div v-for="setting in editableSettings" :class="settingFieldClass(setting.SettingName)" :key="setting.SettingName">
                    <label :for="setting.SettingName">{{ settingDisplayName(setting.SettingName) }}</label>
                    <TextArea v-if="setting.SettingName === 'confirmationSmsTemplate'" :id="setting.SettingName" v-model="setting.SettingValue" rows="5" />
                    <TextArea v-else-if="setting.SettingName === 'appointmentDurationConfig'" :id="setting.SettingName" v-model="setting.SettingValue" rows="18" class="jsonSettingEditor" />
                    <InputText v-else :id="setting.SettingName" v-model="setting.SettingValue"/>
                    <small v-if="settingDescription(setting.SettingName)">{{ settingDescription(setting.SettingName) }}</small>
                </div>
            </div>

            <div v-if="activeSettingsTab === 'booking'" class="durationSimulator">
                <h3>Simulateur de durée</h3>
                <small>Utilise la configuration JSON actuelle (même non sauvegardée).</small>

                <div v-if="durationConfigParseError" class="simError">
                    JSON invalide: {{ durationConfigParseError }}
                </div>

                <div class="simControls">
                    <div class="simField">
                        <label for="simLength">Longueur cheveux</label>
                        <select id="simLength" v-model="simLength">
                            <option value="xs">XS</option>
                            <option value="s">S</option>
                            <option value="m">M</option>
                            <option value="l">L</option>
                            <option value="xl">XL</option>
                        </select>
                    </div>
                    <div class="simField">
                        <label for="simThickness">Epaisseur cheveux</label>
                        <select id="simThickness" v-model="simThickness">
                            <option value="fin">Fins</option>
                            <option value="moyen">Moyen</option>
                            <option value="epais">Epais</option>
                            <option value="nsp">Je ne sais pas</option>
                        </select>
                    </div>
                    <div class="simField" v-if="simServices.includes('coupe')">
                        <label for="simCoupe">Détail coupe</label>
                        <select id="simCoupe" v-model="simCoupeDetails">
                            <option value="coupeComplete">Complète</option>
                            <option value="pointes">Pointes</option>
                        </select>
                    </div>
                    <div class="simField" v-if="simServices.includes('couleur')">
                        <label for="simCouleur">Détail couleur</label>
                        <select id="simCouleur" v-model="simCouleurDetails">
                            <option value="couleurComplete">Complète</option>
                            <option value="racines">Racines</option>
                        </select>
                    </div>
                    <div class="simField" v-if="simServices.includes('mèches')">
                        <label for="simMeches">Détail mèches</label>
                        <select id="simMeches" v-model="simMecheDetails">
                            <option value="mecheComplete">Tête complète</option>
                            <option value="partielles">Partielles</option>
                        </select>
                    </div>
                </div>

                <div class="simServices">
                    <label>Services</label>
                    <div class="simServicesGrid">
                        <div v-for="service in simServiceOptions" :key="service.value" class="simServiceItem">
                            <CheckBox :inputId="`sim-${service.value}`" :value="service.value" v-model="simServices" />
                            <label :for="`sim-${service.value}`">{{ service.label }}</label>
                        </div>
                    </div>
                </div>

                <div v-if="simResult" class="simResult">
                    <div class="simDuration">
                        Longueur finale calculée: <strong>{{ String(simFinalLength).toUpperCase() }}</strong><br />
                        Durée calculée: <strong>{{ formatDuration(simResult.duration) }}</strong> ({{ simResult.duration }} min)
                    </div>
                    <table class="simTraceTable">
                        <thead>
                            <tr>
                                <th>Règle</th>
                                <th>Détail</th>
                                <th>Appliquée</th>
                                <th>Delta</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in simResult.trace" :key="item.id">
                                <td>{{ item.label || item.id }}</td>
                                <td>{{ traceDetail(item) }}</td>
                                <td>{{ item.applied ? 'Oui' : 'Non' }}</td>
                                <td>{{ item.delta > 0 ? `+${item.delta}` : item.delta }}</td>
                                <td>{{ item.runningTotal }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div v-if="activeSettingsTab === 'pricing'" class="priceConfigRoot">
                <div class="priceDiscountRow">
                    <label for="priceDiscountPercent">Réduction par défaut (%)</label>
                    <InputText id="priceDiscountPercent" v-model="priceConfig.discountPercent" />
                </div>
                <div class="priceConfigList">
                    <div class="priceConfigCategory" v-for="category in priceConfig.categories" :key="category.key">
                        <div class="priceConfigCategoryTitle">{{ category.label }}</div>
                        <div class="priceConfigOptions">
                            <div class="priceOptionEditor" v-for="option in category.options" :key="`${category.key}-${option.label}`">
                                <label>{{ option.label }}</label>
                                <div class="p-inputgroup">
                                    <InputText v-model="option.amount" />
                                    <span class="p-inputgroup-addon">CHF</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="activeSettingsTab === 'publicInfo'" class="publicInfoRoot">
                <div class="field checkboxRow">
                    <CheckBox inputId="appointmentInfoShow" v-model="appointmentInfoShow" :binary="true" />
                    <label for="appointmentInfoShow">Afficher le bloc d'information sur le site de rendez-vous</label>
                </div>

                <label>Texte d'information (riche)</label>
                <div class="editorToolbar">
                    <InputButton class="p-button-sm p-button-outlined" label="Gras" @click="applyEditorCommand('bold')"/>
                    <InputButton class="p-button-sm p-button-outlined" label="Italique" @click="applyEditorCommand('italic')"/>
                    <InputButton class="p-button-sm p-button-outlined" label="Retour ligne" @click="insertLineBreak"/>
                </div>
                <div
                    ref="infoEditor"
                    class="richEditor"
                    contenteditable="true"
                    @input="onEditorInput"
                ></div>
                <small>Autorisé: gras, italique, retours de ligne, listes simples.</small>
            </div>
            </template>
            <template #footer>
                 <InputButton icon="pi pi-save" label="Sauvegarder" class="p-button-outlined p-button-secondary" @click="saveSettings"/>
            </template>
        </CardView>

        <CardView class="settingsCard" v-if="activeSettingsTab === 'account'">
        <template #header>
            <h2>Mon compte</h2>
        </template>
        <template #content>
            <GridLoader v-if="accountLoading" class="loader"></GridLoader>

            <div v-if="!accountLoading" class="accountSection">
                <h3>Informations du compte</h3>
                <div class="accountForm">
                    <div class="field">
                        <label for="accountFirstName">Prénom</label>
                        <InputText id="accountFirstName" v-model="accountFirstName" />
                    </div>
                    <div class="field">
                        <label for="accountLastName">Nom</label>
                        <InputText id="accountLastName" v-model="accountLastName" />
                    </div>
                    <div class="field">
                        <label for="accountUsername">Nom d'utilisateur</label>
                        <InputText id="accountUsername" v-model="accountUsername" />
                    </div>
                </div>
                <div class="accountActions">
                    <InputButton icon="pi pi-save" label="Sauvegarder" class="p-button-outlined p-button-secondary" @click="saveAccount" :disabled="accountSaving" />
                </div>
                <MessageItem v-if="accountMessage" :severity="accountMessageSeverity" :closable="false">{{ accountMessage }}</MessageItem>
            </div>

            <div v-if="!accountLoading" class="accountSection passwordSection">
                <h3>Changer le mot de passe</h3>
                <div class="accountForm">
                    <div class="field">
                        <label for="currentPassword">Mot de passe actuel</label>
                        <InputPassword id="currentPassword" v-model="currentPassword" :feedback="false" toggleMask />
                    </div>
                    <div class="field">
                        <label for="newPassword">Nouveau mot de passe</label>
                        <InputPassword id="newPassword" v-model="newPassword" :feedback="false" toggleMask />
                    </div>
                    <div class="field">
                        <label for="confirmPassword">Confirmer le nouveau mot de passe</label>
                        <InputPassword id="confirmPassword" v-model="confirmPassword" :feedback="false" toggleMask />
                    </div>
                </div>
                <div class="accountActions">
                    <InputButton icon="pi pi-lock" label="Changer le mot de passe" class="p-button-outlined p-button-secondary" @click="changePassword" :disabled="passwordSaving" />
                </div>
                <MessageItem v-if="passwordMessage" :severity="passwordMessageSeverity" :closable="false">{{ passwordMessage }}</MessageItem>
            </div>

            <div v-if="!accountLoading" class="accountSection logoutSection">
                <InputButton icon="pi pi-sign-out" label="Déconnexion" class="p-button-outlined p-button-danger" @click="logout" />
            </div>
        </template>
        </CardView>
</div>
</template>

<style scoped>
   .settingsTabs {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin: 10px 0 12px 0;
   }

   .settingsTabButton {
        border-color: #ced9ec !important;
        color: #425574 !important;
        background: #ffffff !important;
   }

   .settingsTabButtonActive {
        border-color: #8eadde !important;
        background: #edf4ff !important;
        color: #2c4569 !important;
        font-weight: 700;
   }

   .settingsCard {
        margin-bottom: 14px;
   }

   .settingsCard :deep(.p-card-header),
   .settingsCard :deep(.p-card-content),
   .settingsCard :deep(.p-card-footer) {
        padding-left: 14px;
        padding-right: 14px;
   }

   .settingsCard :deep(.p-card-content) {
        padding-top: 10px;
        padding-bottom: 12px;
   }

   .settingsSectionTitle {
        margin: 0 0 10px 0;
        font-size: 0.92rem;
        font-weight: 700;
        letter-spacing: 0.02em;
        color: #263754;
   }

   .settingsGrid {
        margin: 0 0 10px 0;
   }

   .settingField {
        margin: 0;
        padding: 4px 10px 8px 0;
   }

   .settingField small {
        margin-top: 5px;
        display: block;
        color: #5b6a83;
   }

   .jsonSettingEditor {
        font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
   }

   .durationSimulator {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid #e0e8f4;
        text-align: left;
   }

   .simError {
        margin: 8px 0;
        color: #a11f1f;
        font-size: 0.9rem;
   }

   .simControls {
        margin-top: 10px;
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 8px;
   }

   .simField label {
        display: block;
        margin-bottom: 4px;
        font-size: 0.82rem;
        color: #60718f;
   }

   .simField select {
        width: 100%;
        padding: 6px 8px;
        border: 1px solid #ccd9ec;
        border-radius: 8px;
        background: #fff;
   }

   .simServices {
        margin-top: 10px;
   }

   .simServicesGrid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 6px 10px;
        margin-top: 4px;
   }

   .simServiceItem {
        display: flex;
        align-items: center;
        gap: 6px;
   }

   .simResult {
        margin-top: 10px;
   }

   .simDuration {
        margin-bottom: 8px;
   }

   .simTraceTable {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.88rem;
   }

   .simTraceTable th,
   .simTraceTable td {
        border: 1px solid #e2e9f5;
        padding: 6px 8px;
        vertical-align: top;
   }

   .simTraceTable th {
        text-align: left;
        background: #f4f8ff;
        color: #344866;
   }


   .appointmentInfoPanel {
        margin-top: 16px;
        text-align: left;
        padding-top: 12px;
        border-top: 1px solid #e0e8f4;
        padding-right: 4px;
   }

   .priceConfigRoot,
   .publicInfoRoot {
        text-align: left;
   }

   .priceDiscountRow {
        display: grid;
        grid-template-columns: minmax(220px, 280px) 120px;
        align-items: center;
        gap: 10px;
        margin-bottom: 12px;
   }

   .priceConfigList {
        display: grid;
        gap: 10px;
   }

   .priceConfigCategory {
        border: 1px solid #e2e9f5;
        border-radius: 10px;
        padding: 10px;
        background: #fff;
   }

   .priceConfigCategoryTitle {
        font-weight: 700;
        margin-bottom: 8px;
        color: #29416f;
   }

   .priceConfigOptions {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 8px 10px;
   }

   .priceOptionEditor label {
        display: block;
        font-size: 0.82rem;
        color: #60718f;
        margin-bottom: 4px;
   }

   .checkboxRow {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 10px;
   }

   .editorToolbar {
        display: flex;
        gap: 8px;
        margin: 8px 0;
   }

   .richEditor {
        min-height: 160px;
        border: 1px solid #ccd9ec;
        border-radius: 10px;
        padding: 12px;
        background: #ffffff;
   }


   @media (max-width: 900px) {
        .settingsTabs {
            gap: 6px;
        }

.priceDiscountRow {
            grid-template-columns: 1fr;
            max-width: 260px;
        }

        .priceConfigOptions {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .simControls,
        .simServicesGrid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
   }

   .accountSection {
        margin-bottom: 24px;
        text-align: left;
   }

   .accountSection h3 {
        margin: 0 0 12px 0;
        font-size: 1.05rem;
        color: #1a335f;
   }

   .accountForm {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 10px 16px;
        margin-bottom: 12px;
   }

   .accountForm .field label {
        display: block;
        margin-bottom: 4px;
        font-size: 0.85rem;
        color: #5b6a83;
   }

   .accountForm .field :deep(.p-inputtext),
   .accountForm .field :deep(.p-password) {
        width: 100%;
   }

   .accountActions {
        margin-bottom: 10px;
   }

   .passwordSection {
        padding-top: 20px;
        border-top: 1px solid #e0e8f4;
   }

   .logoutSection {
        padding-top: 20px;
        border-top: 1px solid #e0e8f4;
   }
</style>

<script setup>
import { ref, onMounted, computed, nextTick } from 'vue';
import axios from 'axios'
import { authHeader } from '../helpers/authHeader.js';
import { computeAppointmentDuration, defaultAppointmentDurationConfig, normalizeAppointmentDurationConfig } from '../helpers/appointmentDurationRules.js';

const settingsLoading = ref(false);
const activeSettingsTab = ref('booking');

const settings = ref([]);
const appointmentInfoShow = ref(false);
const appointmentInfoHtml = ref('');
const priceConfig = ref(defaultPriceConfig());
const infoEditor = ref(null);
const simLength = ref('m');
const simThickness = ref('moyen');
const simServices = ref(['coupe']);
const simCoupeDetails = ref('coupeComplete');
const simCouleurDetails = ref('couleurComplete');
const simMecheDetails = ref('mecheComplete');

// Account management
const accountLoading = ref(false);
const accountSaving = ref(false);
const accountFirstName = ref('');
const accountLastName = ref('');
const accountUsername = ref('');
const accountMessage = ref('');
const accountMessageSeverity = ref('success');
const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const passwordSaving = ref(false);
const passwordMessage = ref('');
const passwordMessageSeverity = ref('success');

const lengthLookup = {
    xs: { fin: 'xs', moyen: 'xs', epais: 's', nsp: 'xs' },
    s: { fin: 's', moyen: 's', epais: 'm', nsp: 'm' },
    m: { fin: 's', moyen: 'm', epais: 'l', nsp: 'l' },
    l: { fin: 'm', moyen: 'l', epais: 'xl', nsp: 'l' },
    xl: { fin: 'm', moyen: 'l', epais: 'xl', nsp: 'xl' }
};

const simServiceOptions = [
    { value: 'coupe', label: 'Coupe' },
    { value: 'brushing', label: 'Brushing' },
    { value: 'couleur', label: 'Couleur' },
    { value: 'mèches', label: 'Mèches' },
    { value: 'lissage', label: 'Lissage' },
    { value: 'permanente', label: 'Permanente' },
    { value: 'barbe', label: 'Barbe' }
];

const hiddenSettingNames = ['appointmentInfoShow', 'appointmentInfoHtml', 'priceCalculatorConfig'];
const editableSettings = computed(() => settings.value.filter(s => !hiddenSettingNames.includes(s.SettingName)));
const durationConfigSetting = computed(() => settings.value.find((s) => s.SettingName === 'appointmentDurationConfig'));

const durationConfigParseError = computed(() => {
    const rawValue = durationConfigSetting.value?.SettingValue;
    if (!rawValue) {
        return '';
    }
    try {
        JSON.parse(rawValue);
        return '';
    } catch (error) {
        return error?.message || 'Erreur de parsing';
    }
});

const simConfig = computed(() => {
    const rawValue = durationConfigSetting.value?.SettingValue;
    if (!rawValue) {
        return defaultAppointmentDurationConfig();
    }
    try {
        return normalizeAppointmentDurationConfig(JSON.parse(rawValue));
    } catch {
        return null;
    }
});

const simResult = computed(() => {
    if (!simConfig.value) {
        return null;
    }
    const finalLength = lengthLookup?.[simLength.value]?.[simThickness.value] || simLength.value;
    return computeAppointmentDuration(simConfig.value, {
        finalLength,
        services: simServices.value,
        details: {
            coupeDetails: simCoupeDetails.value,
            couleurDetails: simCouleurDetails.value,
            mecheDetails: simMecheDetails.value
        }
    });
});

const simFinalLength = computed(() => lengthLookup?.[simLength.value]?.[simThickness.value] || simLength.value);
const simAdditionBreakdown = computed(() => {
    if (!simConfig.value) {
        return [];
    }
    const baseTimes = simConfig.value.baseTimes?.[simFinalLength.value] || {};
    return simServices.value
        .filter((service) => service !== 'brushing')
        .map((service) => ({
            service,
            minutes: Number(baseTimes[service] || 0)
        }));
});

onMounted(() => {
    loadSettings();
});

async function setActiveSettingsTab(tab) {
    activeSettingsTab.value = tab;
    if (tab === 'account') {
        loadAccount();
    }
    if (tab === 'publicInfo') {
        await nextTick();
        if (infoEditor.value && appointmentInfoHtml.value) {
            infoEditor.value.innerHTML = appointmentInfoHtml.value;
        }
    }
}

function loadAccount() {
    accountLoading.value = true;
    accountMessage.value = '';
    passwordMessage.value = '';
    axios.get(`${process.env.VUE_APP_API_URL}/Users/me`, { headers: authHeader() })
        .then(response => {
            accountFirstName.value = response.data.firstName || '';
            accountLastName.value = response.data.lastName || '';
            accountUsername.value = response.data.username || '';
            accountLoading.value = false;
        })
        .catch(() => {
            accountLoading.value = false;
        });
}

function saveAccount() {
    accountSaving.value = true;
    accountMessage.value = '';
    axios.put(`${process.env.VUE_APP_API_URL}/Users/me`, {
        firstName: accountFirstName.value,
        lastName: accountLastName.value,
        username: accountUsername.value
    }, { headers: authHeader() })
        .then(response => {
            accountSaving.value = false;
            accountMessage.value = 'Compte mis à jour';
            accountMessageSeverity.value = 'success';
            // Update stored user info
            const stored = JSON.parse(localStorage.getItem('user') || '{}');
            stored.firstName = response.data.firstName;
            stored.lastName = response.data.lastName;
            stored.username = response.data.username;
            localStorage.setItem('user', JSON.stringify(stored));
        })
        .catch(error => {
            accountSaving.value = false;
            accountMessage.value = error.response?.data?.message || 'Erreur lors de la mise à jour';
            accountMessageSeverity.value = 'error';
        });
}

function changePassword() {
    passwordMessage.value = '';

    if (newPassword.value !== confirmPassword.value) {
        passwordMessage.value = 'Les mots de passe ne correspondent pas';
        passwordMessageSeverity.value = 'error';
        return;
    }

    if (newPassword.value.length < 4) {
        passwordMessage.value = 'Le mot de passe doit contenir au moins 4 caractères';
        passwordMessageSeverity.value = 'error';
        return;
    }

    passwordSaving.value = true;
    axios.post(`${process.env.VUE_APP_API_URL}/Users/change-password`, {
        currentPassword: currentPassword.value,
        newPassword: newPassword.value
    }, { headers: authHeader() })
        .then(() => {
            passwordSaving.value = false;
            passwordMessage.value = 'Mot de passe modifié avec succès';
            passwordMessageSeverity.value = 'success';
            currentPassword.value = '';
            newPassword.value = '';
            confirmPassword.value = '';
        })
        .catch(error => {
            passwordSaving.value = false;
            passwordMessage.value = error.response?.data?.message || 'Erreur lors du changement de mot de passe';
            passwordMessageSeverity.value = 'error';
        });
}

function logout() {
    localStorage.removeItem('user');
    window.location.href = '/login';
}

function tabButtonClass(tab) {
    return activeSettingsTab.value === tab ? 'settingsTabButtonActive' : 'settingsTabButton';
}

function loadSettings() {
    settingsLoading.value = true;

    var options = {
      "headers" :  authHeader()
    };

    axios
      .get(`${process.env.VUE_APP_API_URL}/Settings`, options)
      .then(async response => {
          settingsLoading.value = false;
          settings.value = response.data;
          prettifyJsonSetting('appointmentDurationConfig');

          const showSetting = settings.value.find(s => s.SettingName === 'appointmentInfoShow');
          const htmlSetting = settings.value.find(s => s.SettingName === 'appointmentInfoHtml');
          const priceConfigSetting = settings.value.find(s => s.SettingName === 'priceCalculatorConfig');

          appointmentInfoShow.value = !!showSetting && String(showSetting.SettingValue).toLowerCase() === 'true';
          appointmentInfoHtml.value = htmlSetting?.SettingValue || '';
          priceConfig.value = parsePriceConfig(priceConfigSetting?.SettingValue);

          await nextTick();
          if (infoEditor.value) {
            infoEditor.value.innerHTML = appointmentInfoHtml.value;
          }
      })
      .catch(reason => {
          settingsLoading.value = false;
          console.log(reason);
          alert("error");
      });
}

function onEditorInput() {
    appointmentInfoHtml.value = infoEditor.value ? infoEditor.value.innerHTML : '';
}

function applyEditorCommand(command) {
    if (!infoEditor.value) {
      return;
    }
    infoEditor.value.focus();
    document.execCommand(command, false);
    onEditorInput();
}

function insertLineBreak() {
    if (!infoEditor.value) {
      return;
    }
    infoEditor.value.focus();
    document.execCommand('insertLineBreak');
    onEditorInput();
}

function upsertSetting(name, value) {
    const existing = settings.value.find(s => s.SettingName === name);
    if (existing) {
        existing.SettingValue = value;
        return existing;
    }
    return null;
}

function settingDisplayName(name) {
    if (name === 'minHoursBeforeNextAppointment') {
        return "Délai minimum avant le prochain rendez-vous (heures)";
    }
    if (name === 'confirmationSmsTemplate') {
        return "Template SMS de confirmation";
    }
    if (name === 'appointmentDurationConfig') {
        return "Configuration des règles de durée (JSON)";
    }
    return name;
}

function settingDescription(name) {
    if (name === 'minHoursBeforeNextAppointment') {
        return "Empêche la réservation en ligne trop proche de l'heure actuelle. Exemple: 2 = les clients ne peuvent réserver qu'à partir de maintenant + 2 heures.";
    }
    if (name === 'confirmationSmsTemplate') {
        return "Variables disponibles: {date}, {time}, {duration}, {firstname}, {lastname}, {name}, {phone}.";
    }
    if (name === 'appointmentDurationConfig') {
        return "Config dynamique du calcul de durée (services, règles, arrondi). Garder un JSON valide.";
    }
    return "";
}

function settingFieldClass(name) {
    if (name === 'appointmentDurationConfig') {
        return 'field col-12 settingField';
    }
    return 'field col-12 md:col-6 lg:col-4 settingField';
}

function formatDuration(minutes) {
    const value = Number(minutes || 0);
    if (value < 60) {
        return `${value} min`;
    }
    const hours = Math.floor(value / 60);
    const mins = value % 60;
    return mins === 0 ? `${hours}h` : `${hours}h${String(mins).padStart(2, '0')}`;
}

function serviceLabel(value) {
    const found = simServiceOptions.find((s) => s.value === value);
    return found ? found.label : value;
}

function traceDetail(item) {
    if (!item || !item.id || !simConfig.value) {
        return '';
    }

    if (item.id === 'sum-services-excluding-brushing') {
        if (!simAdditionBreakdown.value.length) {
            return 'Aucun service additionné';
        }
        return simAdditionBreakdown.value
            .map((part) => `${serviceLabel(part.service)} ${part.minutes}m`)
            .join(' + ');
    }

    if (item.id === 'add-brushing-if-needed' && item.applied) {
        const brushing = Number(simConfig.value.baseTimes?.[simFinalLength.value]?.brushing || 0);
        return `Brushing ${brushing}m`;
    }

    if (item.id === 'permanente-extra-dry-time' && item.applied) {
        return 'Séchage +30m';
    }

    if (item.id === 'meches-partielles-without-coupe-couleur' && item.applied) {
        const removed = Number(simConfig.value.tables?.removeMechesPartielles?.[simFinalLength.value]?.withoutCoupeCouleur || 0);
        return `Mèches partielles -${removed}m`;
    }

    if (item.id === 'meches-partielles-with-coupe-couleur' && item.applied) {
        const removed = Number(simConfig.value.tables?.removeMechesPartielles?.[simFinalLength.value]?.withCoupeCouleur || 0);
        return `Mèches partielles (combo) -${removed}m`;
    }

    if (item.id === 'couleur-racines-reduction' && item.applied) {
        const removed = Number(simConfig.value.tables?.removeCouleurRacines?.[simFinalLength.value]?.remove || 0);
        return `Racines -${removed}m`;
    }

    return '';
}

function saveSettings() {
    onEditorInput();

    upsertSetting('appointmentInfoShow', appointmentInfoShow.value ? 'true' : 'false');
    upsertSetting('appointmentInfoHtml', appointmentInfoHtml.value || '');
    upsertSetting('priceCalculatorConfig', JSON.stringify(sanitizePriceConfig(priceConfig.value)));

    if (!validateJsonSetting('appointmentDurationConfig')) {
        return;
    }

    const settingsToSave = settings.value.filter(s => s && s.SettingId);

    var options = {
      "headers" :  authHeader(true)
    };

    settingsLoading.value = true;

    Promise.all(settingsToSave.map((setting) => {
      const json = JSON.stringify({
        SettingName: setting.SettingName,
        SettingValue: setting.SettingValue
      });
      return axios.post(`${process.env.VUE_APP_API_URL}/Settings?settingId=${setting.SettingId}`, json, options);
    }))
      .then(() => {
          settingsLoading.value = false;
      })
      .catch(reason => {
          settingsLoading.value = false;
          console.log(reason);
          alert("error");
      });
}

function prettifyJsonSetting(name) {
    const setting = settings.value.find((s) => s.SettingName === name);
    if (!setting || !setting.SettingValue) {
        return;
    }

    try {
        setting.SettingValue = JSON.stringify(JSON.parse(setting.SettingValue), null, 2);
    } catch {
        // Preserve existing content if not parseable.
    }
}

function validateJsonSetting(name) {
    const setting = settings.value.find((s) => s.SettingName === name);
    if (!setting || !setting.SettingValue) {
        return true;
    }

    try {
        JSON.parse(setting.SettingValue);
        return true;
    } catch {
        alert(`${settingDisplayName(name)} contient un JSON invalide.`);
        return false;
    }
}

function defaultPriceConfig() {
    return {
        discountPercent: 10,
        categories: [
            { key: 'accueil', label: 'Forfait Accueil', options: [{ label: 'Non', amount: 0 }, { label: 'Oui', amount: 5 }] },
            { key: 'coupe', label: 'Forfait Coupe', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 20 }, { label: 'S', amount: 25 }, { label: 'M', amount: 45 }, { label: 'L', amount: 50 }, { label: 'XL', amount: 50 }] },
            { key: 'brushing', label: 'Forfait Brushing', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 10 }, { label: 'S', amount: 15 }, { label: 'M', amount: 20 }, { label: 'L', amount: 30 }, { label: 'XL', amount: 40 }] },
            { key: 'coloration', label: 'Forfait Coloration', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 67 }, { label: 'S', amount: 72 }, { label: 'M', amount: 94 }, { label: 'L', amount: 119 }, { label: 'XL', amount: 140 }] },
            { key: 'meches', label: 'Forfait Mèches', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 72 }, { label: 'S', amount: 78 }, { label: 'M', amount: 85 }, { label: 'L', amount: 125 }, { label: 'XL', amount: 145 }] },
            { key: 'balayage', label: 'Forfait Balayage', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 20 }, { label: 'S', amount: 20 }, { label: 'M', amount: 20 }, { label: 'L', amount: 25 }, { label: 'XL', amount: 25 }] },
            { key: 'lissage', label: 'Forfait Lissage', options: [{ label: 'Non', amount: 0 }, { label: 'S', amount: 112 }, { label: 'M', amount: 148 }, { label: 'L', amount: 170 }, { label: 'XL', amount: 180 }] },
            { key: 'permanente', label: 'Forfait Permanente', options: [{ label: 'Non', amount: 0 }, { label: 'S', amount: 90 }, { label: 'M', amount: 95 }, { label: 'L', amount: 110 }, { label: 'XL', amount: 135 }] },
            { key: 'gloss', label: 'Forfait Gloss', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 5 }, { label: 'S', amount: 5 }, { label: 'M', amount: 10 }, { label: 'L', amount: 15 }, { label: 'XL', amount: 20 }] }
        ]
    };
}

function parsePriceConfig(rawValue) {
    const fallback = defaultPriceConfig();
    if (!rawValue) {
        return fallback;
    }
    try {
        const parsed = JSON.parse(rawValue);
        if (!parsed || !Array.isArray(parsed.categories)) {
            return fallback;
        }
        return sanitizePriceConfig(parsed);
    } catch {
        return fallback;
    }
}

function toNumber(value, fallback = 0) {
    const normalized = String(value ?? '').replace(',', '.').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function sanitizePriceConfig(config) {
    const fallback = defaultPriceConfig();
    const sourceCategories = Array.isArray(config?.categories) ? config.categories : fallback.categories;
    const fallbackByKey = {};
    fallback.categories.forEach((c) => {
        fallbackByKey[c.key] = c;
    });

    const categories = sourceCategories.map((category) => {
        const fallbackCategory = fallbackByKey[category.key] || { key: category.key, label: category.label || category.key, options: [] };
        const sourceOptions = Array.isArray(category.options) ? category.options : fallbackCategory.options;
        return {
            key: category.key || fallbackCategory.key,
            label: category.label || fallbackCategory.label,
            options: sourceOptions.map((option, index) => {
                const fallbackOption = fallbackCategory.options[index] || { label: option.label || `Option ${index + 1}`, amount: 0 };
                return {
                    label: option.label || fallbackOption.label,
                    amount: toNumber(option.amount, fallbackOption.amount)
                };
            })
        };
    });

    return {
        discountPercent: Math.max(0, toNumber(config?.discountPercent, fallback.discountPercent)),
        categories
    };
}


</script>
